import type { SmartEditorAPI } from '../components/SmartCreateEditor';
import type { ContentType } from '../model/ContentTypes';
import type { InputMethod } from '../model/InputMethods';

export type SmartState = {
	prompt: string;
	contentTypes: ContentType[];
	inputMethod: InputMethod | null;
};

type Subscriber = (state: SmartState) => void;

export class SmartCreateStore {
	public api: SmartEditorAPI | null = null;
	private static instance: SmartCreateStore;

	private state = {
		prompt: '',
		contentTypes: [] as ContentType[],
		inputMethod: null as InputMethod | null,
	};
	private subscribers: Subscriber[] = [];

	public static getInstance(): SmartCreateStore {
		if (!SmartCreateStore.instance) {
			SmartCreateStore.instance = new SmartCreateStore();
		}
		return SmartCreateStore.instance;
	}

	public setPrompt(prompt: string) {
		this.state.prompt = prompt;
		this.notifySubscribers();
	}

	public addContentType(contentType: ContentType) {
		if (!this.state.contentTypes.includes(contentType)) {
			this.state.contentTypes.push(contentType);
			this.notifySubscribers();
		}
	}

	public removeContentType(contentType: ContentType) {
		if (!this.state.contentTypes.includes(contentType)) {
			return;
		}
		this.state.contentTypes = this.state.contentTypes.filter((type) => type !== contentType);
		this.notifySubscribers();
	}

	public setContentTypes(contentTypes: ContentType[]) {
		this.state.contentTypes = contentTypes;
		this.notifySubscribers();
	}

	public setInputMethod(inputMethod: InputMethod | null) {
		this.state.inputMethod = inputMethod;
		this.notifySubscribers();
	}

	public setApi(api: SmartEditorAPI | null) {
		this.api = api;
	}

	public getApi(): SmartEditorAPI | null {
		return this.api;
	}

	public subscribe(subscriber: Subscriber) {
		this.subscribers.push(subscriber);
	}
	public unsubscribe(subscriber: Subscriber) {
		this.subscribers = this.subscribers.filter((s) => s !== subscriber);
	}
	private notifySubscribers() {
		this.subscribers.forEach((subscriber) => subscriber(this.state));
	}

	public getState() {
		return this.state;
	}

	public destroy() {
		this.subscribers = [];
		this.state = {
			prompt: '',
			contentTypes: [] as ContentType[],
			inputMethod: null as InputMethod | null,
		};
		this.api = null;
	}
}

export const storePrompt = () => {
	const store = SmartCreateStore.getInstance();
	store.api?.core?.actions.requestDocument((doc) => {
		store.setPrompt(JSON.stringify(doc));
	});
};

export const updatePromptContent = (prompt: string) => {
	const store = SmartCreateStore.getInstance();
	store.api?.core.actions.replaceDocument({
		type: 'doc',
		version: 1,
		content: [{ type: 'paragraph', content: [{ type: 'text', text: prompt }] }],
	});
};

export const addContentType = (contentType: ContentType) => {
	const store = SmartCreateStore.getInstance();
	store.addContentType(contentType);
};

export const removeContentType = (contentType: ContentType) => {
	const store = SmartCreateStore.getInstance();
	store.removeContentType(contentType);
};

export const toggleContentType = (contentType: ContentType) => {
	const store = SmartCreateStore.getInstance();
	if (store.getState().contentTypes.includes(contentType)) {
		store.removeContentType(contentType);
	} else {
		store.addContentType(contentType);
	}
};

export const updateContentTypes = (contentTypes: ContentType[]) => {
	const store = SmartCreateStore.getInstance();
	store.setContentTypes(contentTypes);
};

export const updateInputMethod = (inputMethod: InputMethod | null) => {
	const store = SmartCreateStore.getInstance();
	const currentInputMethod = store.getState().inputMethod;

	if (currentInputMethod === inputMethod) {
		store.setInputMethod(null);
	} else {
		store.setInputMethod(inputMethod);
	}
};

export const setAPI = (api: SmartEditorAPI | null) => {
	const store = SmartCreateStore.getInstance();
	store.setApi(api);
};

export const getAPI = (): SmartEditorAPI | null => {
	const store = SmartCreateStore.getInstance();
	return store.getApi();
};
